import { useQuery } from "@tanstack/react-query";
import { getEventEntitlements } from "@/lib/api/clients/EventClient";
import { qk } from "@/lib/query/queryKeys";
import type {
  EventCompanyRole,
  EventEntitlements,
  EventEntitlementsEmailQuota,
  EventEntitlementsFeatures,
  EventEntitlementsSms,
} from "@/types/EventEntitlements";

/**
 * Plan features, mail quota, AI credits, company name and the caller's company
 * role for one event — read from the company that OWNS it.
 *
 * Replaces `fetchSubscriptions(...)`, `getCompany("emailQuota,…")`,
 * `useIsCompanyOwner()` and `useCompanyRoleString()` everywhere an event is in
 * scope. Those resolve the company from the login cookie, so on an event
 * belonging to another company they answered for the wrong tenant — and for a
 * same-company EDITOR / VIEWER the subscriptions read is refused, which every
 * gate then showed as an upgrade wall.
 *
 * Goes through the CLIENT, not a handler, so a failure throws and the query
 * lands in `isError` instead of resolving to a ServerError object the callers
 * would have to sniff. Same choice `useEventGuestQuota` makes. Keyed by the
 * event id so `invalidateEventScoped` matches it.
 */
export function useEventEntitlements(eventId: string | null) {
  return useQuery<EventEntitlements>({
    queryKey: eventId ? qk.events.entitlements(eventId) : ["events", "entitlements", "none"],
    // `enabled` guards the call; the fallback only satisfies the type.
    queryFn: () => getEventEntitlements(eventId ?? ""),
    enabled: !!eventId,
    staleTime: 30_000,
  });
}

/**
 * Three-way status, never a bare boolean: a pending or failed read must render
 * a neutral disabled control or an error with retry — NEVER the upgrade lock
 * (rule R4 of the event-scope wave). Only a successful `false` locks.
 */
export type EntitlementStatus = "loading" | "error" | "ready";

export interface EventFeatureState {
  status: EntitlementStatus;
  /** `null` unless `status === "ready"`. */
  value: boolean | null;
}

/** One plan-gated feature of the event's company. `eventId === null` → stays "loading". */
export function useEventFeature(
  eventId: string | null,
  name: keyof EventEntitlementsFeatures,
): EventFeatureState {
  const q = useEventEntitlements(eventId);
  // DATA FIRST. react-query keeps the last good answer when a background
  // refetch fails (window focus, 30 s stale) and sets `isError` beside it.
  // Reading `isError` first turned that into "error": the sender page swapped
  // to an error screen and closed whatever dialog was open — typically right
  // after the user came back from another tab with the DNS records. A cached
  // answer stays the answer; "error" is only for having nothing at all.
  if (!q.data) return { status: q.isError ? "error" : "loading", value: null };
  // A body without the flag (a backend that does not know it yet) is "don't
  // know", not `false`: only a boolean the server actually sent may lock.
  const flag = q.data.features?.[name];
  if (typeof flag !== "boolean") return { status: "error", value: null };
  return { status: "ready", value: flag };
}

/** A finite number, or "no value" (absent, null, NaN — anything else). */
const finiteOf = (n: unknown): number | undefined =>
  typeof n === "number" && Number.isFinite(n) ? n : undefined;

/**
 * Mails the event's company can still send — the ONE "remaining" figure of
 * the purchased-emails wave (model B, two pots), shared with main-consumer's
 * debit and main-backend's `EventEntitlementsService.emailRemaining` through
 * `src/lib/__fixtures__/email-remaining-parity.json`:
 *
 *   remaining = max(0, max(0, quota − used) + purchasedEmails)
 *
 * each 0 when absent or null. The plan part floors at 0 (after a downgrade
 * `used` can sit above `quota`; that overdraw is not paid out of the balance),
 * a balance without a plan quota IS a figure, a negative balance (hand edit)
 * lowers the figure but never below 0.
 *
 * `undefined` = nothing to say, and every reader gates on `!== undefined`
 * (no quota row, no banner, no send lock — SCN-5): no section (the caller
 * lacks `sent-emails:send`), or nothing metered — `quota` null/absent AND no
 * numeric `purchasedEmails`. "Unmetered" has two wire shapes, `quota: null`
 * and — from a serializer that drops null members — no `quota` key; both are
 * read loosely so no `undefined - used` NaN can reach the dialog.
 *
 * The backend sends its own `remaining` by the same formula; when the key is
 * on the wire it is the answer (a `null` there is the server's "no figure" —
 * it comes beside a `purchasedEmails: 0` the local rule would otherwise read
 * as a numeric balance and print "0 available"). The formula runs here only
 * for a body without the key, i.e. a backend from before that wave.
 * Never returns NaN, never a negative number, always a whole one.
 *
 * The cockpit's ONLY copy of the formula: every surface that shows the figure
 * or gates Send on it (send dialog row, the 1f Send lock, the mail-template
 * banners) takes it from here.
 */
export function remainingEmailsOf(
  emailQuota: Partial<EventEntitlementsEmailQuota> | null | undefined,
): number | undefined {
  if (emailQuota == null) return undefined;
  if ("remaining" in emailQuota && emailQuota.remaining !== undefined) {
    if (emailQuota.remaining === null) return undefined;
    const server = finiteOf(emailQuota.remaining);
    if (server !== undefined) return Math.max(0, Math.floor(server));
  }
  const quota = finiteOf(emailQuota.quota);
  const balance = finiteOf(emailQuota.purchasedEmails);
  if (quota === undefined && balance === undefined) return undefined;
  // Floors like the consumer (email-remaining.ts): a fractional counter (hand
  // edits only) must not show "10.5 available" where the debit lets 10 pass.
  const planLeft = Math.max(0, Math.floor((quota ?? 0) - (finiteOf(emailQuota.used) ?? 0)));
  return Math.max(0, Math.floor(planLeft + (balance ?? 0)));
}

/**
 * SMS parts the event's company can still send — the ONE figure of
 * `src/lib/__fixtures__/sms-remaining-parity.json` (byte copy of
 * main-consumer's SOURCE; main-backend `EventEntitlementsService` answers the
 * same `remaining`):
 *
 *   remaining = max(0, max(0, monthlyCap − monthlyUsed) + balance)
 *
 * each 0 when absent, null or not a finite number; whole numbers, never
 * negative. `undefined` when there is no `sms` section at all (the caller
 * lacks `workflows:write`, or a backend from before the SMS wave). The server's
 * own `remaining` wins when it is a finite number.
 */
export function remainingSmsOf(
  sms: Partial<EventEntitlementsSms> | null | undefined,
): number | undefined {
  if (sms == null) return undefined;
  const server = finiteOf(sms.remaining);
  if (server !== undefined) return Math.max(0, Math.floor(server));
  const capLeft = Math.max(0, Math.floor((finiteOf(sms.monthlyCap) ?? 0) - (finiteOf(sms.monthlyUsed) ?? 0)));
  return Math.max(0, Math.floor(capLeft + (finiteOf(sms.balance) ?? 0)));
}

/**
 * Does the event's company have an SMS allowance at all (`monthlyCap > 0`)?
 * What enables SMS for the company — the balance alone does not.
 */
export function smsAllowanceOpen(sms: Partial<EventEntitlementsSms> | null | undefined): boolean {
  const cap = finiteOf(sms?.monthlyCap);
  return cap !== undefined && cap > 0;
}

export interface EventCompanyRoleState {
  status: EntitlementStatus;
  /** The caller holds a company_roles row in the event's company. `false` unless ready. */
  member: boolean;
  /** The caller's role IN THE EVENT'S company — not the event role. `null` unless ready and a member. */
  role: EventCompanyRole | null;
  /** The owning company's name, for "ask {company}" copy. `null` unless ready. */
  companyName: string | null;
  /**
   * May write the company-owned things reached through this event (sender
   * addresses, CRM pool, template libraries, GDPR artefacts): a member with
   * company role OWNER / ADMIN / EDITOR. `false` while loading or on error.
   */
  canWriteCompanyAssets: boolean;
  /** OWNER of the event's company: plan purchase, email-domain and address-identity writes. */
  isCompanyOwner: boolean;
}

const KNOWN_ROLES: ReadonlySet<string> = new Set(["OWNER", "ADMIN", "EDITOR", "VIEWER"]);
const WRITER_ROLES: ReadonlySet<EventCompanyRole> = new Set(["OWNER", "ADMIN", "EDITOR"]);

/** `null` for explicit null, a dropped key, or a string that is not a company role. */
function normalizeRole(raw: unknown): EventCompanyRole | null {
  if (typeof raw !== "string") return null;
  const upper = raw.toUpperCase();
  return KNOWN_ROLES.has(upper) ? (upper as EventCompanyRole) : null;
}

export function useEventCompanyRole(eventId: string | null): EventCompanyRoleState {
  const q = useEventEntitlements(eventId);
  // Data first — see useEventFeature: a failed background refetch must not
  // take the rights of a caller whose last good answer is still in hand.
  if (!q.data) {
    return {
      status: q.isError ? "error" : "loading",
      member: false, role: null, companyName: null, canWriteCompanyAssets: false, isCompanyOwner: false,
    };
  }
  // `viewer` / `company` are null for an orphan event; `companyRole` / `name`
  // are null (or, from a serializer that drops nulls, missing) for an outsider
  // or an unnamed company. Loose reads only — see EventEntitlements.ts.
  const member = q.data.viewer?.companyMember === true;
  const role = member ? normalizeRole(q.data.viewer?.companyRole) : null;
  return {
    status: "ready",
    member,
    role,
    companyName: q.data.company?.name ?? null,
    canWriteCompanyAssets: role != null && WRITER_ROLES.has(role),
    isCompanyOwner: role === "OWNER",
  };
}
